import React from 'react';
import { Typography, Alert, Table, Tag, Card, Space, Button } from 'antd';
import { DownloadOutlined, CopyOutlined } from '@ant-design/icons';

const { Text } = Typography;

interface OligoResult {
  index: number;  // 后端返回的是index而不是id
  sequence: string;
  start: number;
  end: number;
  length: number;
  orientation: string;  // F或R
  overlap_tm_to_prev?: number;
  overlap_tm_to_next?: number;
}

interface GeneSynthesisResultProps {
  taskResult: any;
  data?: any;
  title?: string;
  showTitle?: boolean;
}

const GeneSynthesisResult: React.FC<GeneSynthesisResultProps> = ({ 
  taskResult, 
  data, 
  title = "基因合成引物设计结果", 
  showTitle = true 
}) => {

  // 提取引物设计结果
  const getOligoResults = (): OligoResult[] => {
    console.log('GeneSynthesisResult - taskResult:', taskResult);
    console.log('GeneSynthesisResult - data:', data);
    
    // 格式1: data参数（优先使用）
    if (data) {
      // data.oligos 直接包含数组
      if (data.oligos && Array.isArray(data.oligos)) {
        console.log('✓ 找到 data.oligos:', data.oligos.length);
        return data.oligos;
      }
      
      // data.results[].result.oligos
      if (data.results && Array.isArray(data.results)) {
        const allOligos: OligoResult[] = [];
        for (const item of data.results) {
          if (item.result?.oligos && Array.isArray(item.result.oligos)) {
            allOligos.push(...item.result.oligos);
          } else if (item.oligos && Array.isArray(item.oligos)) {
            allOligos.push(...item.oligos);
          }
        }
        if (allOligos.length > 0) {
          console.log('✓ 找到 data.results[].oligos:', allOligos.length);
          return allOligos;
        }
      }
    }
    
    // 格式2: taskResult.result格式（Profile页面格式）
    if (taskResult?.result) {
      // taskResult.result.oligos 直接包含数组
      if (taskResult.result.oligos && Array.isArray(taskResult.result.oligos)) {
        console.log('✓ 找到 taskResult.result.oligos:', taskResult.result.oligos.length);
        return taskResult.result.oligos;
      }
      
      // taskResult.result.results[].result.oligos
      if (taskResult.result.results && Array.isArray(taskResult.result.results)) {
        const allOligos: OligoResult[] = [];
        for (const item of taskResult.result.results) {
          if (item.result?.oligos && Array.isArray(item.result.oligos)) {
            allOligos.push(...item.result.oligos);
          } else if (item.oligos && Array.isArray(item.oligos)) {
            allOligos.push(...item.oligos);
          }
        }
        if (allOligos.length > 0) {
          console.log('✓ 找到 taskResult.result.results[].oligos:', allOligos.length);
          return allOligos;
        }
      }
    }
    
    // 格式3: taskResult.results（旧格式）
    if (taskResult?.results && Array.isArray(taskResult.results)) {
      const allOligos: OligoResult[] = [];
      for (const item of taskResult.results) {
        if (item.result?.oligos && Array.isArray(item.result.oligos)) {
          allOligos.push(...item.result.oligos);
        } else if (item.oligos && Array.isArray(item.oligos)) {
          allOligos.push(...item.oligos);
        }
      }
      if (allOligos.length > 0) {
        console.log('✓ 找到 taskResult.results[].oligos:', allOligos.length);
        return allOligos;
      }
    }
    
    // 格式4: taskResult.oligos（直接格式）
    if (taskResult?.oligos && Array.isArray(taskResult.oligos)) {
      console.log('✓ 找到 taskResult.oligos:', taskResult.oligos.length);
      return taskResult.oligos;
    }
    
    console.log('✗ 未找到任何oligos数据');
    return [];
  };

  // 复制序列到剪贴板
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  // 下载所有引物序列
  const downloadAllOligos = () => {
    const oligos = getOligoResults();
    if (oligos.length === 0) return;
    
    const content = oligos.map((oligo) => 
      `>Oligo_${oligo.index}_${oligo.orientation}_${oligo.start}-${oligo.end}\n${oligo.sequence}`
    ).join('\n\n');
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'gene_synthesis_primers.fasta';
    a.click();
    URL.revokeObjectURL(url);
  };

  const oligoResults = getOligoResults();

  if (!taskResult) {
    return <Alert type="warning" message="无任务数据" />;
  }

  if (oligoResults.length === 0) {
    return (
      <Alert
        type="info"
        message="无基因合成引物设计结果"
        description="任务可能还在运行中或者结果格式不正确"
        showIcon
      />
    );
  }

  const columns = [
    {
      title: 'Index',
      dataIndex: 'index',
      key: 'index',
      width: 60,
    },
    {
      title: '方向',
      dataIndex: 'orientation',
      key: 'orientation',
      width: 60,
      render: (orientation: string) => (
        <Tag color={orientation === 'F' ? 'blue' : 'green'}>
          {orientation}
        </Tag>
      )
    },
    {
      title: '位置',
      key: 'position',
      width: 100,
      render: (record: OligoResult) => `${record.start}-${record.end}`,
    },
    {
      title: '长度',
      dataIndex: 'length',
      key: 'length',
      width: 80,
      render: (length: number) => `${length} bp`
    },
    {
      title: '重叠Tm (°C)',
      key: 'overlap_tm',
      width: 140,
      render: (record: OligoResult) => {
        const parts = [];
        if (record.overlap_tm_to_prev) {
          parts.push(`前: ${record.overlap_tm_to_prev.toFixed(1)}°C`);
        }
        if (record.overlap_tm_to_next) {
          parts.push(`后: ${record.overlap_tm_to_next.toFixed(1)}°C`);
        }
        return (
          <Text style={{ fontSize: '12px' }}>
            {parts.join(' / ')}
          </Text>
        );
      }
    },
    {
      title: '序列',
      dataIndex: 'sequence',
      key: 'sequence',
      render: (sequence: string) => (
        <div style={{ maxWidth: 200 }}>
          <Text 
            code 
            style={{ 
              fontSize: '11px', 
              wordBreak: 'break-all',
              display: 'block',
              maxHeight: '40px',
              overflow: 'hidden'
            }}
          >
            {sequence.length > 30 ? `${sequence.substring(0, 30)}...` : sequence}
          </Text>
        </div>
      )
    },
    {
      title: '操作',
      key: 'actions',
      width: 100,
      render: (record: OligoResult) => (
        <Button
          type="link"
          size="small"
          icon={<CopyOutlined />}
          onClick={() => copyToClipboard(record.sequence)}
        >
          复制
        </Button>
      )
    }
  ];

  return (
    <div>
      {showTitle && (
        <div style={{ marginBottom: 16 }}>
          <Text strong>{title}</Text>
          <Text type="secondary" style={{ marginLeft: 8 }}>({oligoResults.length} 个引物)</Text>
        </div>
      )}

      <Card>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Text strong>引物设计概览</Text>
            <Text type="secondary" style={{ marginLeft: 8 }}>
              共设计 {oligoResults.length} 个引物
            </Text>
          </div>
          <Button 
            type="primary" 
            icon={<DownloadOutlined />}
            onClick={downloadAllOligos}
          >
            下载所有引物
          </Button>
        </div>

        <Table
          dataSource={oligoResults}
          columns={columns}
          rowKey="id"
          pagination={{ pageSize: 10, showSizeChanger: false }}
          size="small"
          scroll={{ x: 800 }}
        />
      </Card>
    </div>
  );
};

export default GeneSynthesisResult;
